import { Fragment, useEffect, useState } from 'react';

import { SparkInput } from '../components/SparkInput';
import { SparkMessage } from '../components/SparkMessage';
import { useAuth } from '../context/AuthContext';
import { ApiError, listPNRs } from '../lib/api';
import type { PNRSummary } from '../lib/types';

/** PNRs filed from the workspace: every one for root, otherwise the agent's own. */
export function PNRsPage() {
  const { user } = useAuth();
  const isRoot = user?.role === 'root';

  const [pnrs, setPnrs] = useState<PNRSummary[]>([]);
  const [search, setSearch] = useState('');
  const [pccFilter, setPccFilter] = useState('');
  const [open, setOpen] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      listPNRs(search.trim(), isRoot ? pccFilter.trim() : '')
        .then((result) => {
          if (!cancelled) {
            setPnrs(result.pnrs);
            setError('');
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : 'The PNR list could not be loaded.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [search, pccFilter, isRoot]);

  const columns = isRoot ? 8 : 7;
  const rowKey = (pnr: PNRSummary) => `${pnr.pcc}-${pnr.locator}`;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-header__title">PNRs</h1>
          <p className="page-header__subtitle">
            {isRoot ? 'Every PNR filed from this system' : 'The PNRs you have created'}, most recently changed first.
            Retrieve one in the workspace with <span className="mono">*LOCATOR</span>.
          </p>
        </div>
      </div>

      {error && <SparkMessage variant="error">{error}</SparkMessage>}

      <div className="toolbar">
        <SparkInput
          label="Search"
          placeholder="Record locator or passenger last name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {isRoot && (
          <SparkInput
            label="PCC"
            placeholder="All agencies"
            value={pccFilter}
            onChange={(event) => setPccFilter(event.target.value)}
          />
        )}
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Locator</th>
              {isRoot && <th>PCC</th>}
              <th>Passengers</th>
              <th>Itinerary</th>
              <th>Travel date</th>
              <th>Created by</th>
              <th>Filed</th>
              <th>Last changed</th>
            </tr>
          </thead>
          <tbody>
            {loading && pnrs.length === 0 && (
              <tr>
                <td colSpan={columns} className="table__empty">
                  Loading PNRs…
                </td>
              </tr>
            )}
            {!loading && pnrs.length === 0 && (
              <tr>
                <td colSpan={columns} className="table__empty">
                  {search || pccFilter ? 'No PNRs match this search.' : 'No PNRs have been filed yet.'}
                </td>
              </tr>
            )}
            {pnrs.map((pnr) => {
              const key = rowKey(pnr);
              const isOpen = open === key;
              return (
                <Fragment key={key}>
                  <tr
                    className={`pnr-row${isOpen ? ' pnr-row--open' : ''}`}
                    onClick={() => setOpen(isOpen ? '' : key)}
                    aria-expanded={isOpen}
                  >
                    <td className="mono">{pnr.locator}</td>
                    {isRoot && <td className="mono">{pnr.pcc}</td>}
                    <td>{passengers(pnr)}</td>
                    <td className="mono">{route(pnr) || '—'}</td>
                    <td className="mono">{pnr.itinerary[0]?.date || '—'}</td>
                    <td>{pnr.filedBy || pnr.agent || '—'}</td>
                    <td>{new Date(pnr.filedAt).toLocaleString()}</td>
                    <td>{new Date(pnr.updatedAt).toLocaleString()}</td>
                  </tr>
                  {isOpen && (
                    <tr className="pnr-detail">
                      <td colSpan={columns}>
                        <PNRDetail pnr={pnr} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PNRDetail({ pnr }: { pnr: PNRSummary }) {
  const names = pnr.travelers.map((t) => `${t.number} ${t.last}/${t.first}${t.ptc ? ` *${t.ptc}` : ''}`);
  const contacts = [...pnr.phones, pnr.ticketing && `TKT ${pnr.ticketing}`, pnr.receivedFrom && `RCVD-${pnr.receivedFrom}`];
  const segments = pnr.itinerary.map(
    (s) =>
      `${s.line} ${s.carrier}${s.number.padStart(4)}${s.class} ${s.date} ${s.origin}${s.destination} ` +
      `${s.status}${s.seats} ${s.depart} ${s.arrive}${s.airlineLocator ? ` ${s.carrier}/${s.airlineLocator}` : ''}`,
  );
  const quotes = pnr.priceQuotes.map(
    (q) => `PQ${q.number} ${q.paxType} ${q.total}${q.ticketBy ? ` TKT BY ${q.ticketBy}` : ''}`,
  );

  return (
    <div className="pnr-detail__grid">
      <DetailBlock label="Names" lines={names} />
      <DetailBlock label="Contacts and ticketing" lines={contacts} />
      <DetailBlock label="Itinerary" lines={segments} wide />
      <DetailBlock label="Price quotes" lines={quotes} wide />
    </div>
  );
}

function DetailBlock({ label, lines, wide }: { label: string; lines: (string | false)[]; wide?: boolean }) {
  const shown = lines.filter(Boolean);
  return (
    <div className={wide ? 'pnr-detail__block pnr-detail__block--wide' : 'pnr-detail__block'}>
      <div className="pnr-detail__label">{label}</div>
      <div className="mono pnr-detail__lines">{shown.length > 0 ? shown.join('\n') : '—'}</div>
    </div>
  );
}

function passengers(pnr: PNRSummary): string {
  if (pnr.travelers.length === 0) return '—';
  const [first, ...rest] = pnr.travelers;
  const lead = `${first.last}/${first.first}`;
  return rest.length > 0 ? `${lead} +${rest.length}` : lead;
}

/** The journey as city pairs, joining connections: DAC-DXB-LHR. */
function route(pnr: PNRSummary): string {
  const points: string[] = [];
  for (const seg of pnr.itinerary) {
    if (points[points.length - 1] !== seg.origin) points.push(seg.origin);
    points.push(seg.destination);
  }
  return points.join('-');
}
